import clsx from 'clsx'
import type {
  AriaAttributes,
  KeyboardEventHandler,
  MouseEventHandler,
  PropsWithChildren,
  Ref,
} from 'react'
import React, { forwardRef } from 'react'
import { toDataSet } from '../utilities/toDataSet'

export interface IconButtonProps {
  /**
   * Accessible label for the icon button.
   * Required for screen readers
   */
  'aria-label': AriaAttributes['aria-label']
  /**
   * Identifies the element whose contents or presence are controlled by the icon button
   */
  'aria-controls'?: AriaAttributes['aria-controls']
  /**
   * Indicates whether the element controlled by the icon button is expanded or collapsed
   */
  'aria-expanded'?: AriaAttributes['aria-expanded']
  /**
   * Indicates the availability and type of interactive popup element that can be triggered by the button
   */
  'aria-haspopup'?: AriaAttributes['aria-haspopup']
  /**
   * The color scheme of the icon button
   * @default 'default'
   */
  color?: IconButtonColor
  /**
   * Custom data attributes to be added to the button element
   */
  dataSet?: Record<string, string | undefined>
  /**
   * If true, the button is disabled
   * @default false
   */
  disabled?: boolean
  /**
   * The id attribute of the button element
   */
  id?: string
  /**
   * Callback fired when the button is activated
   */
  onClick?: MouseEventHandler<HTMLButtonElement>
  /**
   * Callback fired when a key is pressed while the button is focused
   */
  onKeyDown?: KeyboardEventHandler<HTMLButtonElement>
  /**
   * The size of the icon button
   * @default 'small'
   */
  size?: IconButtonSize
  /** Tab order index for keyboard navigation.
   * Only adjust this if you want the user’s tab order to be non-standard or removed entirely
   * @default 0
   */
  tabIndex?: number
  /** The type attribute of the button element
   * @default 'button'
   */
  type?: IconButtonType
}

type IconButtonColor = 'error' | 'default' | 'inverse' | 'primary' | 'secondary'

type IconButtonColors = {
  [key in IconButtonColor]: string
}

type IconButtonSize = 'large' | 'medium' | 'small'

type IconButtonSizes = {
  [key in IconButtonSize]: string
}

type IconButtonType = 'button' | 'reset' | 'submit'

const colors: IconButtonColors = {
  error: `text-error-main hover:bg-error-10 focus:bg-error-10 focus-visible:ring active:bg-error-20 disabled:text-black-25`,
  default: `text-black-60 hover:bg-black-5 focus:bg-black-5 focus-visible:ring active:bg-black-15 disabled:text-black-25`,
  inverse: `text-white-100 hover:bg-white-25 focus:bg-white-25 focus-visible:ring active:bg-white-45 disabled:text-white-30`,
  primary: `text-primary-main hover:bg-primary-10 focus:bg-primary-10 focus-visible:ring active:bg-primary-20 disabled:text-black-25`,
  secondary: `text-secondary-main hover:bg-secondary-10 focus:bg-secondary-10 focus-visible:ring active:bg-secondary-20 disabled:text-black-25`,
}

const sizes: IconButtonSizes = {
  large: 'w-15 h-15 p-3',
  medium: 'w-12 h-12 p-3',
  small: 'w-7 h-7 p-0.5',
}

export function Component(
  {
    'aria-label': label,
    'aria-controls': controls,
    'aria-expanded': expanded,
    'aria-haspopup': hasPopup,
    children,
    color = 'default',
    dataSet,
    disabled = false,
    id,
    onClick,
    onKeyDown,
    size = 'small',
    tabIndex = 0,
    type = 'button',
  }: PropsWithChildren<IconButtonProps>,
  ref?: Ref<HTMLButtonElement>,
): JSX.Element {
  return (
    <button
      aria-controls={controls}
      aria-expanded={expanded}
      aria-haspopup={hasPopup}
      aria-label={label}
      className={clsx(
        'shrink-0 rounded-full disabled:bg-transparent disabled:cursor-not-allowed focus:outline-none',
        colors[color],
        sizes[size],
      )}
      {...toDataSet(dataSet)}
      disabled={disabled}
      id={id}
      onClick={onClick}
      onKeyDown={onKeyDown}
      ref={ref}
      tabIndex={tabIndex}
      type={type}
    >
      {children}
    </button>
  )
}

const IconButton = forwardRef<
  HTMLButtonElement,
  PropsWithChildren<IconButtonProps>
>(Component)

IconButton.displayName = 'IconButton'

export default IconButton
